/**
 * Tunnel de prospection — étape 1 (§3.5 du plan).
 *
 * **Rien n'est recalculé a posteriori.** Chaque étage est un horodatage écrit au moment où il se
 * produit, dans `milestones`. Une stat est une lecture, jamais une déduction : reconstituer les
 * dates depuis `calls[]` se tromperait dès qu'un document est modifié.
 *
 * ```
 * importés ─▶ appelables ─▶ contactés ─▶ qualifiés ─▶ demandes créées ─▶ CONVERTIS (Pro)
 * ```
 *
 * À l'étape 1, les deux derniers étages valent `0` : c'est le câblage pour les étapes 2 et 3,
 * pas un bug. La distinction `demande_creee` / `converti` est la raison d'être du fichier :
 * **une demande de création n'est pas une conversion.**
 *
 * Fonction **pure** : elle ne reçoit que des `Date` (jamais de `Timestamp` Firestore) et ne
 * dépend ni de React ni de Firebase.
 */

const aJalon = (prospect, cle) => Boolean(prospect && prospect.milestones && prospect.milestones[cle]);

/** `null` si le dénominateur est 0 — un taux sur zéro fiche n'existe pas. */
const taux = (numerateur, denominateur) =>
  denominateur > 0 ? Math.round((numerateur / denominateur) * 1000) / 10 : null;

/** Médiane, pas moyenne : une seule conversion à 9 mois fausse une moyenne sur 3 clients. */
const mediane = (valeurs) => {
  if (!Array.isArray(valeurs) || valeurs.length === 0) return null;
  const triees = [...valeurs].sort((a, b) => a - b);
  const milieu = Math.floor(triees.length / 2);
  return triees.length % 2 === 0 ? (triees[milieu - 1] + triees[milieu]) / 2 : triees[milieu];
};

const enJours = (debut, fin) => {
  if (!(debut instanceof Date) || !(fin instanceof Date)) return null;
  const millisecondes = fin.getTime() - debut.getTime();
  if (Number.isNaN(millisecondes) || millisecondes < 0) return null;
  return millisecondes / (24 * 60 * 60 * 1000);
};

/** Statuts qui sortent du dénominateur dès leur passage (numéros faux, hors cible, refus RGPD). */
export const STATUTS_HORS_DENOMINATEUR = ['exclu', 'opposition'];

/**
 * @param {Array} prospects documents de **la même cohorte** (`source.runDate`)
 * @returns {{imported: number, callable: number, contacted: number, qualified: number,
 *            requests: number, converted: number,
 *            rates: {contactRate: number|null, qualificationRate: number|null,
 *                    acceptanceRate: number|null, conversionRate: number|null,
 *                    globalConversionRate: number|null},
 *            medianDelayDays: number|null,
 *            counts: {excluded: number, opposition: number, lost: number}}}
 */
export const computeFunnel = (prospects) => {
  const liste = Array.isArray(prospects) ? prospects.filter(Boolean) : [];

  const imported = liste.length;

  const horsDenominateur = liste.filter(
    (prospect) =>
      STATUTS_HORS_DENOMINATEUR.includes(prospect.status) || prospect.optedOut === true
  );
  const appelables = liste.filter(
    (prospect) =>
      !STATUTS_HORS_DENOMINATEUR.includes(prospect.status) && prospect.optedOut !== true
  );

  const callable = appelables.length;
  const contacted = appelables.filter((prospect) => aJalon(prospect, 'firstCallAt')).length;
  const qualified = appelables.filter((prospect) => aJalon(prospect, 'qualifiedAt')).length;
  const requests = liste.filter((prospect) => aJalon(prospect, 'requestCreatedAt')).length;
  const converted = liste.filter((prospect) => aJalon(prospect, 'proActivatedAt')).length;

  const delais = liste
    .map((prospect) =>
      enJours(prospect.milestones?.requestCreatedAt, prospect.milestones?.proActivatedAt)
    )
    .filter((delai) => delai !== null);

  return {
    imported,
    callable,
    contacted,
    qualified,
    requests,
    converted,
    rates: {
      // Qualité du numéro scrapé
      contactRate: taux(contacted, callable),
      // Qualité de la liste (métier, zone) — les `perdu` restent au dénominateur
      qualificationRate: taux(qualified, contacted),
      // Qualité de l'argumentaire d'appel
      acceptanceRate: taux(requests, qualified),
      // Le seul chiffre qui compte : une demande créée n'est pas une conversion
      conversionRate: taux(converted, requests),
      // Coût réel d'un client
      globalConversionRate: taux(converted, imported)
    },
    medianDelayDays: mediane(delais),
    counts: {
      excluded: liste.filter((prospect) => prospect.status === 'exclu').length,
      opposition: liste.filter((prospect) => prospect.status === 'opposition').length,
      lost: liste.filter((prospect) => prospect.status === 'perdu').length,
      horsDenominateur: horsDenominateur.length
    }
  };
};

/**
 * Cohortes disponibles pour le sélecteur de l'onglet Stats.
 * Les stats sont **toujours** calculées sur une cohorte figée = un `source.runDate` : sinon un
 * prospect importé en janvier et converti en avril fait bouger les chiffres tout seuls.
 *
 * @returns {Array<{runDate: string, count: number}>} du plus récent au plus ancien
 */
export const listerCohortes = (prospects) => {
  const liste = Array.isArray(prospects) ? prospects : [];
  const parDate = new Map();

  liste.forEach((prospect) => {
    const runDate = prospect?.source?.runDate || 'inconnue';
    parDate.set(runDate, (parDate.get(runDate) || 0) + 1);
  });

  return [...parDate.entries()]
    .map(([runDate, count]) => ({ runDate, count }))
    .sort((a, b) => String(b.runDate).localeCompare(String(a.runDate)));
};

/** Filtre la cohorte : c'est la seule façon autorisée de lire des stats. */
export const filtrerCohorte = (prospects, runDate) => {
  const liste = Array.isArray(prospects) ? prospects : [];
  if (!runDate) return liste;
  return liste.filter((prospect) => (prospect?.source?.runDate || 'inconnue') === runDate);
};

/** Affichage : `—` quand le taux n'existe pas, jamais `0 %`. */
export const formaterTaux = (valeur) => {
  if (valeur === null || valeur === undefined) return '—';
  return `${String(valeur).replace('.', ',')} %`;
};
